import { useParams, useSearchParams } from 'react-router-dom';
import { Footer, TitleBar } from '../../../components';
import TitleUpdater from '../../../components/common/TitleUpdater';
import { useItApplicationName } from '../../../hooks';
import { ApplicationReportCard } from '../components/ApplicationReportCard';
import { ApplicationReportOverviewCard } from '../components/ApplicationReportOverviewCard';
import { useQuery } from '@apollo/client';
import { GET_IT_APPLICATION_WITH_WORKAROUNDS } from '../../cyber/pages/workarounds/queries';
import { useState } from 'react';
import {
  ItApplication,
  ITApplicationWithWorkarounds,
  Workaround,
  WorkaroundDefinedType,
} from '../../cyber/pages/workarounds/types';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { CollapsibleText } from '../../../components/generic/TextTruncation';
import { renderComplianceBadges } from '../../cyber/utils/complianceBadgeRenderer';
import { ComplianceStatusEnum } from '../../../shared/types/business-functions-workarounds';
import { TableColumnsType } from 'antd';

export const CyberHubItApplicationReport = () => {
  const [itApplication, setItApplication] = useState<ItApplication | null>(
    null,
  );

  let [searchParams] = useSearchParams();
  const locationId = searchParams.get('locationId')!;

  const { applicationUUID } = useParams();

  const applicationName = useItApplicationName(applicationUUID!);

  const { error, loading } = useQuery<ITApplicationWithWorkarounds>(
    GET_IT_APPLICATION_WITH_WORKAROUNDS,
    {
      variables: { uuid: applicationUUID, locationUUID: locationId },
      onCompleted: ({ getITApplicationWithWorkarounds: itApplication }) => {
        setItApplication(itApplication);
      },
    },
  );

  const createWorkaroundColumns = (
    titleColumnName: string,
  ): TableColumnsType<Workaround> => [
    {
      title: titleColumnName,
      width: 200,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      sorter: (a: any, b: any) => tableSortingMethods.string(a.name, b.name),
      render: (name: string) => (
        <span className="custom-table--column ml-10">{name}</span>
      ),
    },
    {
      title: 'Runtime with workaround',
      width: 200,
      dataIndex: 'runtimeHours',
      key: 'runtimeHours',
      align: 'center',
      sorter: (a: any, b: any) =>
        tableSortingMethods.number(a.runtimeHours, b.runtimeHours),
      render: (runtimeHours: number | null) => {
        let runtime: string = `${runtimeHours} hours`;
        if (!runtimeHours) {
          runtime = WorkaroundDefinedType.NO_WORKAROUND;
        } else if (runtimeHours === Number.MAX_VALUE) {
          runtime = 'N/A';
        }
        return runtime === WorkaroundDefinedType.NO_WORKAROUND ? (
          <span className="text-[#BABABA]">{runtime}</span>
        ) : (
          <span className="custom-table--column">{runtime}</span>
        );
      },
    },
    {
      title: (
        <span>
          Workaround <br />
          Timeline Compliance
        </span>
      ),
      width: 200,
      dataIndex: 'workaroundTimelineCompliance',
      key: 'workaroundTimelineCompliance',
      align: 'center',
      render: (complianceData: Record<string, ComplianceStatusEnum>) => {
        return renderComplianceBadges(complianceData, {
          isWorkaroundNeeded: true,
        });
      },
    },
    {
      title: 'Workaround Description',
      dataIndex: 'workaroundDescription',
      key: 'workaroundDescription',
      sorter: (a, b) =>
        tableSortingMethods.string(
          a.workaroundDescription,
          b.workaroundDescription,
        ),
      width: 200,
      align: 'left',
      render: (description: string) => {
        return (
          <div className="text-sm font-normal text-black">
            <CollapsibleText text={description} />
          </div>
        );
      },
    },
  ];

  // Generate column definitions using the reusable function
  const businessFunctionsColumns =
    createWorkaroundColumns('Business Functions');

  const title = 'Application Report';

  return (
    <div>
      <TitleUpdater title={title} />
      <TitleBar title={title} subtitle={applicationName} />
      <ApplicationReportOverviewCard
        applicationUUID={applicationUUID!}
        locationUUID={locationId}
      />
      <br />
      <ApplicationReportCard
        applicationUUID={applicationUUID!}
        locationUUID={locationId}
      />
      {itApplication?.businessFunctions &&
        itApplication.businessFunctions.length > 0 && (
          <TableV3
            title="Business Functions"
            columns={businessFunctionsColumns}
            data={itApplication?.businessFunctions}
            loading={loading}
            useBorderedSection
            tableSize="small"
          />
        )}
      <Footer goBack />
    </div>
  );
};
